import type {
  WellboreMapData,
  WellborePoint,
  WellborePointType,
} from 'types/api-responses';
import type { SectionLine } from './atoms';

/** Feet per degree of latitude, close enough at pad scale. */
export const FT_PER_DEGREE_LAT = 364000;

/** How far a drag pulls the points around it when nothing else is chosen, in feet. */
export const DEFAULT_DRAG_INFLUENCE_FT = 2500;
export const MAX_DRAG_INFLUENCE_FT = 10000;

// The section line opens 5,000 ft either side of the lateral, a 10,000 ft slice.
const SECTION_HALF_LENGTH_FT = 5000;

export type Position = [number, number];

/** A point as the map works on it: a position and a role, never a depth. */
export interface WorkingPoint {
  /** The row it was read from; absent on a point this edit added. */
  id?: number;
  lon: number;
  lat: number;
  type: WellborePointType;
  /** The backend has pinned this position; dragging it is refused until unlocked. */
  locationLocked: boolean;
  /** A typed depth, kept as an anchor through a save; null leaves it to the derive. */
  tvdFt: number | null;
  /** Inserted on the map in this edit, so it keeps its grip among the stations. */
  added?: boolean;
  /** The depths the point last read, carried so the section can preview the edit. */
  readTvdFt?: number | null;
  readTvdssFt?: number | null;
}

/**
 * Feet per degree of longitude at a latitude.
 * @param {number} lat The latitude, in degrees.
 * @return {number} The scale.
 */
export const ftPerDegreeLon = (lat: number): number =>
  FT_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180);

/**
 * A filed position as a working point; only the surface hole opens locked.
 * @param {Position} position The position, lon first.
 * @param {WellborePointType} type The point's role.
 * @return {WorkingPoint} The working point.
 */
const stickPoint = (
  [lon, lat]: Position,
  type: WellborePointType
): WorkingPoint => ({
  lon,
  lat,
  type,
  locationLocked: type === 'shl',
  tvdFt: null,
});

/**
 * The filed stick as working points, for a backend that serves no derived points.
 * @param {WellboreMapData | undefined} bore The subject bore from the wells query.
 * @return {WorkingPoint[]} The stick's vertices, or the two filed ends, or none.
 */
export const stickWorkingPoints = (
  bore: WellboreMapData | undefined
): WorkingPoint[] => {
  const geometry = bore?.stick?.geometry;
  const line =
    geometry && geometry.type === 'LineString' ? geometry.coordinates : null;
  if (line && line.length >= 2) {
    return line.map(([lon, lat], index) =>
      stickPoint(
        [lon, lat],
        index === 0 ? 'shl' : index === line.length - 1 ? 'bhl' : 'unassigned'
      )
    );
  }
  // No stick on file: the two filed ends still make an editable straight line.
  const shl = bore?.surfaceHole?.coordinates;
  const bhl = bore?.bottomHole?.coordinates;
  if (!shl || !bhl) return [];
  return [
    stickPoint([shl[0], shl[1]], 'shl'),
    stickPoint([bhl[0], bhl[1]], 'bhl'),
  ];
};

/**
 * Smoothstep falloff: 1 at the dragged vertex, 0 at the anchor that bounds it. An
 * unbounded side follows the drag rigidly, as in the mock.
 * @param {number} distance Along-line distance from the dragged vertex.
 * @param {number} range Distance to the nearest anchor on that side.
 * @return {number} The weight, between 0 and 1.
 */
const falloffWeight = (distance: number, range: number): number => {
  if (range === Infinity) return 1;
  if (range <= 0 || distance >= range) return 0;
  if (distance <= 0) return 1;
  const t = distance / range;
  return 1 - t * t * (3 - 2 * t);
};

/**
 * Cumulative along-line distance to each vertex, in feet; the plan-view stand-in for
 * MD, so the drag influence can be set in the units the operator thinks in.
 * @param {WorkingPoint[]} points The working points, in order.
 * @return {number[]} The distance to each, from the first.
 */
const alongLineDistances = (points: WorkingPoint[]): number[] => {
  let total = 0;
  return points.map((point, index) => {
    if (index > 0) {
      const previous = points[index - 1];
      total += Math.hypot(
        (point.lon - previous.lon) * ftPerDegreeLon(point.lat),
        (point.lat - previous.lat) * FT_PER_DEGREE_LAT
      );
    }
    return total;
  });
};

/**
 * Move one vertex and pull its neighbours along with a diminishing effect, so the bore
 * stays smooth. The ends and the landing point anchor the falloff, which also stops at
 * the drag influence so a dense survey can be nudged a station or two at a time.
 * @param {WorkingPoint[]} points The working points.
 * @param {number} index The vertex being dragged.
 * @param {number} deltaLon Longitude delta applied to the dragged vertex.
 * @param {number} deltaLat Latitude delta applied to the dragged vertex.
 * @param {number} influenceFt How far along the bore the pull reaches, in feet.
 * @return {WorkingPoint[]} The moved points.
 */
export const moveWithFalloff = (
  points: WorkingPoint[],
  index: number,
  deltaLon: number,
  deltaLat: number,
  influenceFt = Infinity
): WorkingPoint[] => {
  const distances = alongLineDistances(points);
  const isAnchor = (at: number) =>
    at === 0 || at === points.length - 1 || points[at].type === 'lp';
  let before = -Infinity;
  let after = Infinity;
  points.forEach((_point, at) => {
    if (at === index || !isAnchor(at)) return;
    if (distances[at] < distances[index]) {
      before = Math.max(before, distances[at]);
    } else if (distances[at] > distances[index]) {
      after = Math.min(after, distances[at]);
    }
  });
  const rangeBefore = Math.min(distances[index] - before, influenceFt);
  const rangeAfter = Math.min(after - distances[index], influenceFt);

  return points.map((point, at) => {
    if (at === index) {
      return { ...point, lon: point.lon + deltaLon, lat: point.lat + deltaLat };
    }
    if (isAnchor(at)) return point;
    const gap = distances[at] - distances[index];
    const weight =
      gap < 0
        ? falloffWeight(-gap, rangeBefore)
        : falloffWeight(gap, rangeAfter);
    if (weight <= 0) return point;
    return {
      ...point,
      lon: point.lon + deltaLon * weight,
      lat: point.lat + deltaLat * weight,
    };
  });
};

/**
 * Where the map opens: the subject's surface hole, or its stick's first vertex.
 * @param {WellboreMapData | undefined} bore The subject bore, when the query found one.
 * @return {Position | null} The centre, or null when the bore has no position.
 */
export const centerOf = (
  bore: WellboreMapData | undefined
): Position | null => {
  if (bore?.surfaceHole) {
    const [lon, lat] = bore.surfaceHole.coordinates;
    return [lon, lat];
  }
  const geometry = bore?.stick?.geometry;
  if (geometry?.type === 'LineString' && geometry.coordinates[0]) {
    const [lon, lat] = geometry.coordinates[0];
    return [lon, lat];
  }
  return null;
};

/**
 * The served points as the map works on them: a position and a role, never a depth.
 * @param {WellborePoint[]} points The points as read.
 * @return {WorkingPoint[]} The working copy.
 */
export const toWorking = (points: WellborePoint[]): WorkingPoint[] =>
  points.map((point) => ({
    id: point.id ?? undefined,
    lon: point.longitude,
    lat: point.latitude,
    type: point.pointType,
    locationLocked: point.locationLocked,
    tvdFt: point.depthSource === 'user' ? point.tvdFt : null,
    readTvdFt: point.tvdFt,
    readTvdssFt: point.tvdssFt,
  }));

/**
 * Whether two point lists agree, so Done Editing can skip a commit that changes nothing.
 * @param {WorkingPoint[]} left One list.
 * @param {WorkingPoint[]} right The other.
 * @return {boolean} True when every position and role matches, in order.
 */
export const samePoints = (
  left: WorkingPoint[],
  right: WorkingPoint[]
): boolean =>
  left.length === right.length &&
  left.every(
    (point, index) =>
      point.lon === right[index].lon &&
      point.lat === right[index].lat &&
      point.type === right[index].type
  );

/**
 * Offsets a position by feet east and north, close enough at pad scale.
 * @param {Position} from The starting position.
 * @param {number} eastFt Feet east, negative for west.
 * @param {number} northFt Feet north, negative for south.
 * @return {Position} The offset position.
 */
const offsetFt = (
  from: Position,
  eastFt: number,
  northFt: number
): Position => {
  const [lon, lat] = from;
  return [
    lon + eastFt / ftPerDegreeLon(lat),
    lat + northFt / FT_PER_DEGREE_LAT,
  ];
};

/**
 * The default section line: perpendicular to the lateral, through its midpoint,
 * 5,000 ft either side. A vertical bore (both ends together) gets an east–west line.
 * @param {WorkingPoint[]} points The bore's points, surface hole first.
 * @return {SectionLine | null} The line, or null with fewer than two points.
 */
export const defaultSectionLine = (
  points: WorkingPoint[]
): SectionLine | null => {
  if (points.length < 2) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const mid: Position = [
    (first.lon + last.lon) / 2,
    (first.lat + last.lat) / 2,
  ];
  const eastFt = (last.lon - first.lon) * ftPerDegreeLon(mid[1]);
  const northFt = (last.lat - first.lat) * FT_PER_DEGREE_LAT;
  const length = Math.hypot(eastFt, northFt);
  // Perpendicular unit vector; a zero-length bore falls back to east–west.
  const [perpEast, perpNorth] =
    length === 0 ? [1, 0] : [-northFt / length, eastFt / length];
  return {
    a: offsetFt(
      mid,
      perpEast * SECTION_HALF_LENGTH_FT,
      perpNorth * SECTION_HALF_LENGTH_FT
    ),
    b: offsetFt(
      mid,
      -perpEast * SECTION_HALF_LENGTH_FT,
      -perpNorth * SECTION_HALF_LENGTH_FT
    ),
  };
};
